/**
 * Simple configuration manager that loads config from /config API endpoint
 */

import type {
  ConfigManagerState,
  ConfigLoadOptions,
  AppConfig,
} from './types';

import {
  DEFAULT_CONFIG,
  CONFIG_ENDPOINT,
  CONFIG_TIMEOUT,
  CONFIG_RETRY_ATTEMPTS,
  CONFIG_RETRY_DELAY,
} from './defaults';

/**
 * Simple ConfigManager class for loading configuration from API
 */
export class ConfigManager {
  private static instance: ConfigManager | null = null;
  private state: ConfigManagerState;

  /**
   * Private constructor to enforce singleton pattern
   */
  private constructor() {
    this.state = {
      isLoaded: false,
      isLoading: false,
      config: null,
      error: null,
      lastUpdated: 0,
    };
  }

  /**
   * Get the singleton instance of ConfigManager
   */
  public static getInstance(): ConfigManager {
    if (!ConfigManager.instance) {
      ConfigManager.instance = new ConfigManager();
    }
    return ConfigManager.instance;
  }

  /**
   * Load configuration from /config endpoint
   * @param options - Configuration loading options
   * @returns Promise that resolves to the loaded configuration
   */
  public async loadConfig(options: ConfigLoadOptions = {}): Promise<AppConfig> {
    // Return cached config if already loaded
    if (this.state.config && this.state.isLoaded) {
      return this.state.config;
    }

    this.setState({ isLoading: true, error: null });

    try {
      const config = await this.fetchConfig(options);
      
      this.setState({
        isLoaded: true,
        isLoading: false,
        config,
        error: null,
        lastUpdated: Date.now(),
      });

      console.log('[ConfigManager] Configuration loaded successfully:', config);
      return config;

    } catch (error) {
      console.error('[ConfigManager] Failed to load configuration, using defaults:', error);
      
      // Use default config as fallback
      this.setState({
        isLoaded: true,
        isLoading: false,
        config: DEFAULT_CONFIG,
        error: error as Error,
        lastUpdated: Date.now(),
      });

      return DEFAULT_CONFIG;
    }
  }

  /**
   * Refresh the configuration by reloading from API
   * @param options - Configuration loading options
   * @returns Promise that resolves to the refreshed configuration
   */
  public async refresh(options: ConfigLoadOptions = {}): Promise<AppConfig> {
    this.setState({ isLoaded: false });
    return this.loadConfig(options);
  }

  /**
   * Get the current configuration
   * If not loaded, automatically fetch it
   * @returns Current configuration or null if loading fails
   */
  public async getConfig(): Promise<AppConfig> {
    // If config is not loaded yet, trigger loading
    if (!this.state.config || !this.state.isLoaded) {
      // Don't block - start loading asynchronously
      return this.loadConfig();
    }
    return Promise.resolve(this.state.config);
  }

  /**
   * Get the current configuration manager state
   * @returns Current state
   */
  public getState(): ConfigManagerState {
    return { ...this.state };
  }

  /**
   * Check if configuration is currently loading
   * @returns True if loading, false otherwise
   */
  public isLoading(): boolean {
    return this.state.isLoading;
  }

  /**
   * Check if configuration has been loaded
   * @returns True if loaded, false otherwise
   */
  public isLoaded(): boolean {
    return this.state.isLoaded;
  }

  /**
   * Private helper methods
   */

  private async fetchConfig(options: ConfigLoadOptions): Promise<AppConfig> {
    const endpoint = options.configEndpoint || CONFIG_ENDPOINT;
    const timeout = options.timeout || CONFIG_TIMEOUT;
    const cacheKey = `tn_config_cache:${endpoint}`;
    const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

    // Try to read cached config from localStorage first
    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) {
        const parsed = JSON.parse(raw) as { value: AppConfig; expiresAt: number };
        if (parsed && parsed.expiresAt && parsed.expiresAt > Date.now()) {
          console.log('[ConfigManager] Using cached configuration from localStorage');
          return parsed.value;
        } else {
          // expired cache, remove it
          localStorage.removeItem(cacheKey);
        }
      }
    } catch (err) {
      // ignore cache read errors and proceed to fetch
      console.warn('[ConfigManager] Failed to read config cache:', err);
    }
    
    for (let attempt = 1; attempt <= CONFIG_RETRY_ATTEMPTS; attempt++) {
      try {
        console.log(`[ConfigManager] Attempting to load config from: ${endpoint} (attempt ${attempt})`);
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        
        const response = await fetch(endpoint, {
          signal: controller.signal,
          headers: {
            'Accept': 'application/json',
            'Cache-Control': 'no-cache',
          },
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const config = await response.json();
        
        // Basic validation - ensure required fields exist
        if (!config.API_BASE_URL) {
          throw new Error('Invalid config: missing API_BASE_URL');
        }
        
        const finalConfig = {
          ...DEFAULT_CONFIG,
          ...config,
        };

        // Cache the fetched config in localStorage for future calls
        try {
          const payload = { value: finalConfig, expiresAt: Date.now() + CACHE_TTL_MS };
          localStorage.setItem(cacheKey, JSON.stringify(payload));
          console.log('[ConfigManager] Cached configuration to localStorage (30m)');
        } catch (err) {
          console.warn('[ConfigManager] Failed to write config cache:', err);
        }

        return finalConfig;
        
      } catch (error) {
        console.warn(`[ConfigManager] Failed to load config (attempt ${attempt}/${CONFIG_RETRY_ATTEMPTS}):`, error);
        
        if (attempt < CONFIG_RETRY_ATTEMPTS) {
          await this.delay(CONFIG_RETRY_DELAY);
        } else {
          throw error;
        }
      }
    }
    
    throw new Error('All config loading attempts failed');
  }

  private setState(updates: Partial<ConfigManagerState>): void {
    this.state = { ...this.state, ...updates };
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
